'use client';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ArrowLeft, RotateCcw, Trophy, Clock } from 'lucide-react';

interface Props { onBack: () => void; }

interface Card { id: number; emoji: string; isFlipped: boolean; isMatched: boolean; }

const EMOJIS = ['🦁', '🐯', '🦊', '🐺', '🦝', '🐻', '🐼', '🦄', '🐸', '🦋', '🌟', '🎯'];

const LEADERBOARD = [
  { name: 'Yuki H.', time: '0:42', moves: 18, score: 4800 },
  { name: 'Priya S.', time: '0:58', moves: 22, score: 4200 },
  { name: 'Arjun K.', time: '1:05', moves: 25, score: 3900 },
  { name: 'Marcus T.', time: '1:18', moves: 28, score: 3500 },
  { name: 'Sofia R.', time: '1:35', moves: 32, score: 3100 },
];

function createDeck(pairs: number): Card[] {
  const selected = EMOJIS.slice(0, pairs);
  const doubled = [...selected, ...selected];
  const shuffled = doubled.sort(() => Math.random() - 0.5);
  return shuffled.map((emoji, i) => ({ id: i, emoji, isFlipped: false, isMatched: false }));
}

export default function MemoryMatchGame({ onBack }: Props) {
  const [cards, setCards] = useState<Card[]>(() => createDeck(8));
  const [flipped, setFlipped] = useState<number[]>([]);
  const [moves, setMoves] = useState(0);
  const [matches, setMatches] = useState(0);
  const [isChecking, setIsChecking] = useState(false);
  const [gameStarted, setGameStarted] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [xp, setXp] = useState(0);
  const [difficulty, setDifficulty] = useState<4 | 6 | 8>(8);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const totalPairs = difficulty;

  useEffect(() => {
    if (gameStarted && !gameOver) {
      timerRef.current = setInterval(() => setElapsed(e => e + 1), 1000);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [gameStarted, gameOver]);

  const formatTime = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  const handleCardClick = useCallback((idx: number) => {
    if (isChecking || cards[idx].isFlipped || cards[idx].isMatched || flipped.length === 2) return;
    if (!gameStarted) setGameStarted(true);

    const newCards = cards.map((c, i) => i === idx ? { ...c, isFlipped: true } : c);
    const newFlipped = [...flipped, idx];
    setCards(newCards);
    setFlipped(newFlipped);

    if (newFlipped.length === 2) {
      setIsChecking(true);
      setMoves(m => m + 1);
      const [a, b] = newFlipped;
      if (newCards[a].emoji === newCards[b].emoji) {
        setTimeout(() => {
          setCards(prev => prev.map((c, i) => newFlipped.includes(i) ? { ...c, isMatched: true } : c));
          const newMatches = matches + 1;
          setMatches(newMatches);
          setFlipped([]);
          setIsChecking(false);
          if (newMatches === totalPairs) {
            setGameOver(true);
            if (timerRef.current) clearInterval(timerRef.current);
            const score = Math.max(0, 5000 - elapsed * 10 - moves * 20);
            setXp(prev => prev + score);
          }
        }, 400);
      } else {
        setTimeout(() => {
          setCards(prev => prev.map((c, i) => newFlipped.includes(i) && !c.isMatched ? { ...c, isFlipped: false } : c));
          setFlipped([]);
          setIsChecking(false);
        }, 900);
      }
    }
  }, [cards, flipped, isChecking, gameStarted, matches, totalPairs, elapsed, moves]);

  const resetGame = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    setCards(createDeck(difficulty));
    setFlipped([]);
    setMoves(0);
    setMatches(0);
    setIsChecking(false);
    setGameStarted(false);
    setGameOver(false);
    setElapsed(0);
  }, [difficulty]);

  const changeDifficulty = (d: 4 | 6 | 8) => {
    setDifficulty(d);
    if (timerRef.current) clearInterval(timerRef.current);
    setCards(createDeck(d));
    setFlipped([]);
    setMoves(0);
    setMatches(0);
    setIsChecking(false);
    setGameStarted(false);
    setGameOver(false);
    setElapsed(0);
  };

  const cols = difficulty === 4 ? 4 : difficulty === 6 ? 4 : 4;

  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        <button onClick={onBack} className="btn-ghost p-2 rounded-lg"><ArrowLeft size={18} /></button>
        <div>
          <h1 className="text-2xl font-bold text-foreground">🃏 Memory Match</h1>
          <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>Flip cards and find all matching pairs</p>
        </div>
        <div className="ml-auto flex gap-2">
          <button onClick={resetGame} className="btn-ghost flex items-center gap-2 text-sm">
            <RotateCcw size={16} /> Reset
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          {/* Difficulty */}
          <div className="flex gap-2 mb-4">
            {([4, 6, 8] as const).map((d) => (
              <button key={d} onClick={() => changeDifficulty(d)}
                className="px-4 py-2 rounded-lg text-sm font-semibold transition-all"
                style={{
                  background: difficulty === d ? '#06B6D4' : 'var(--card)',
                  color: difficulty === d ? '#fff' : 'var(--muted-foreground)',
                  border: `1px solid ${difficulty === d ? '#06B6D4' : 'var(--border)'}`,
                }}>
                {d === 4 ? 'Easy (8)' : d === 6 ? 'Medium (12)' : 'Hard (16)'}
              </button>
            ))}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-3 mb-5">
            {[
              { label: 'Moves', value: moves, icon: '🎯', color: '#7C3AED' },
              { label: 'Time', value: formatTime(elapsed), icon: '⏱️', color: '#06B6D4' },
              { label: 'Pairs', value: `${matches}/${totalPairs}`, icon: '✅', color: '#10B981' },
            ].map((s) => (
              <div key={s.label} className="rounded-xl p-3 text-center" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
                <p className="text-xl font-bold" style={{ color: s.color }}>{s.value}</p>
                <p className="text-xs mt-1" style={{ color: 'var(--muted-foreground)' }}>{s.icon} {s.label}</p>
              </div>
            ))}
          </div>

          {/* Game over banner */}
          {gameOver && (
            <div className="rounded-xl p-4 mb-5 text-center"
              style={{ background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.4)' }}>
              <p className="text-xl font-bold" style={{ color: '#10B981' }}>🎉 You matched all pairs!</p>
              <p className="text-sm mt-1" style={{ color: 'var(--muted-foreground)' }}>
                {moves} moves · {formatTime(elapsed)} · +{Math.max(0, 5000 - elapsed * 10 - moves * 20)} XP
              </p>
              <button onClick={resetGame} className="btn-accent mt-3 text-sm">Play Again</button>
            </div>
          )}

          {/* Card grid */}
          <div className={`grid gap-3`} style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
            {cards.map((card, idx) => (
              <button
                key={card.id}
                onClick={() => handleCardClick(idx)}
                disabled={card.isMatched || isChecking}
                className="aspect-square rounded-xl flex items-center justify-center text-3xl transition-all duration-300"
                style={{
                  background: card.isMatched
                    ? 'rgba(16,185,129,0.2)'
                    : card.isFlipped
                    ? 'rgba(6,182,212,0.15)'
                    : 'var(--card)',
                  border: `2px solid ${card.isMatched ? '#10B981' : card.isFlipped ? '#06B6D4' : 'var(--border)'}`,
                  transform: card.isFlipped || card.isMatched ? 'rotateY(0deg)' : 'rotateY(180deg)',
                  cursor: card.isMatched ? 'default' : 'pointer',
                }}
              >
                {card.isFlipped || card.isMatched ? card.emoji : '❓'}
              </button>
            ))}
          </div>
        </div>

        {/* Leaderboard */}
        <div className="rounded-2xl p-5" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
          <h3 className="font-bold text-foreground mb-4 flex items-center gap-2">
            <Trophy size={16} style={{ color: '#F59E0B' }} /> Best Times
          </h3>
          <div className="space-y-3">
            {LEADERBOARD.map((entry, i) => (
              <div key={entry.name} className="flex items-center gap-3 p-3 rounded-xl"
                style={{ background: i === 0 ? 'rgba(245,158,11,0.1)' : 'var(--muted)', border: `1px solid ${i === 0 ? 'rgba(245,158,11,0.3)' : 'transparent'}` }}>
                <span className="text-base font-bold w-6 text-center"
                  style={{ color: i === 0 ? '#F59E0B' : i === 1 ? '#94A3B8' : i === 2 ? '#CD7F32' : 'var(--muted-foreground)' }}>
                  {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `${i + 1}`}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-foreground truncate">{entry.name}</p>
                  <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                    <Clock size={10} className="inline mr-1" />{entry.time} · {entry.moves} moves
                  </p>
                </div>
                <span className="text-sm font-bold" style={{ color: '#06B6D4' }}>{entry.score.toLocaleString()}</span>
              </div>
            ))}
          </div>
          <div className="mt-4 p-3 rounded-xl text-center" style={{ background: 'rgba(6,182,212,0.1)', border: '1px solid rgba(6,182,212,0.2)' }}>
            <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Session XP</p>
            <p className="text-xl font-bold" style={{ color: '#06B6D4' }}>{xp.toLocaleString()}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
